<script>

    import dressing from "@intellectability/platform-common";
    import {Button, Modal, toast} from "@intellectability/platform-common/sv";
    import {Collapse} from "@sveltestrap/sveltestrap";

    let {allowUpdate = false, imageURLStub = $bindable(), initials, onupdate, profileID} = $props();

    let showProfileImageModal = $state(false);

    let temporaryImage = $state(null);
    let temporaryImageURL = $state(null);
    let fileInput = $state(null);

    // append to the image src to allow us to reset the browser cache for that image
    // the image path is not changed on upload so we do this to force the change
    let cacheController = $state(new Date().getTime());

    function handleFileSelect(event) {
        uploadError = false;
        const file = event.target.files[0];

        // free image in memory
        if (temporaryImageURL) {
            URL.revokeObjectURL(temporaryImageURL);
        }

        if (file) {
            temporaryImage = file;
            // Create a preview URL for the selected image
            temporaryImageURL = URL.createObjectURL(file);
        }
    }

    function triggerFileInput() {
        fileInput?.click();
    }

    function clearSelection() {

        // free image in memory
        if (temporaryImageURL) {
            URL.revokeObjectURL(temporaryImageURL);
        }

        temporaryImage = null;
        temporaryImageURL = null;
        if (fileInput) {
            fileInput.value = "";
        }
    }

    function handleModalClose() {
        clearSelection();
        showProfileImageModal = false;
    }

    let submitting = $state(false);
    let uploadError = $state(false);

    function handleSave() {
        if (temporaryImage) {

            submitting = true;

            const reader = new FileReader();

            reader.onload = function (event) {
                const base64String = event.target.result;

                // Submit the base64 string to the server
                dressing.post(`/users/photo/upload/${profileID}`, {
                    user_id: profileID,
                    photo: base64String
                }).then(({data: res}) => {
                    if (res.success === true) {

                        // Show success message
                        toast.success("Profile photo updated successfully");

                        cacheController = new Date().getTime();

                        typeof onupdate === "function" && onupdate();

                        // Close modal and clear selection
                        handleModalClose();
                    }
                    else {
                        uploadError = true;
                    }
                })
                    .catch(() => uploadError = true)
                    .finally(() => submitting = false);
            };

            reader.onerror = function () {
                submitting = false;
                uploadError = true;
                toast.error("Error reading the selected file");
            };

            // Read the file as base64
            reader.readAsDataURL(temporaryImage);
        }
    }

    // Clean up object URL when component is destroyed or image changes
    $effect(() => {
        return () => {
            if (temporaryImageURL) {
                URL.revokeObjectURL(temporaryImageURL);
            }
        };
    });
</script>

<div class="position-relative mx-auto my-3 my-md-4" style="width: fit-content; height: fit-content;">
    <wa-avatar
            id="profile-photo"
            {initials}
            image={dressing.api.uri().replace("api/v1", "") + imageURLStub + "?r=" + cacheController}
            shape="circle"
            style="--size: 12rem;"
    ></wa-avatar>
    {#if allowUpdate}
        <div class="position-absolute bottom-0 end-0">
            <!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_static_element_interactions -->
            <wa-icon-button id="edit-profile-photo"
                            name="pencil"
                            style="--background-color-hover: rgba(var(--hrst-secondary-500-rgb), 0.5);"
                            onclick={() => showProfileImageModal = true}
            ></wa-icon-button>
            <wa-tooltip
                    for="edit-profile-photo"
                    showDelay={350}
                    placement="end"
                    distance={50}
            >
                Edit profile image
            </wa-tooltip>
        </div>
    {/if}
</div>

<Modal bind:showModal={showProfileImageModal} title="Upload profile image">

    <div class="d-flex flex-column align-items-center py-4">

        <div class=" mb-3">
            <wa-avatar
                    {initials}
                    image={temporaryImageURL}
                    shape="circle"
                    style="--size: 12rem;"
            ></wa-avatar>
        </div>
        <div>
            <!-- File Input (Hidden) -->
            <input
                    bind:this={fileInput}
                    type="file"
                    accept="image/png, image/jpeg, image/jpg"
                    onchange={handleFileSelect}
                    style="display: none;"
                    multiple={false}
            />

            <!-- File Info -->
            <div class="text-center text-muted mb-3">
                <small>
                    {#if temporaryImage}
                        Previewing: {temporaryImage.name} ({(temporaryImage.size / 1024).toFixed(1)} KB)
                    {:else}
                        No file selected
                    {/if}
                </small>
            </div>

            <!-- Upload Controls -->
            <div class="mb-4">
                <wa-button-group label="Example Button Group">
                    <!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_static_element_interactions -->
                    <wa-button variant="brand" appearance="filled" onclick={triggerFileInput}
                               disabled={submitting}
                               loading={submitting}
                    >
                        <wa-icon slot="prefix" name="image-user"></wa-icon>
                        Choose Photo
                    </wa-button>
                    <!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_static_element_interactions -->
                    <wa-button variant="danger" appearance="filled" onclick={clearSelection}
                               disabled={!temporaryImage || submitting}>
                        <wa-icon name="times"></wa-icon>
                    </wa-button>
                </wa-button-group>
            </div>
        </div>
        <Collapse isOpen={!submitting && uploadError}>
            <div class="mb-3">
                <wa-callout variant="danger" appearance="filled" show={!submitting && uploadError}>
                    There was an error uploading your photo. Please try again or contact support.
                </wa-callout>
            </div>
        </Collapse>
    </div>
    <!-- Action Buttons -->
    <div class="d-flex justify-content-between mx-3 mb-3">
        <Button onclick={handleModalClose} variant="neutral" appearance="filled">
            Cancel
        </Button>
        <Button onclick={handleSave} variant="brand" disabled={!temporaryImage}>
            Save
        </Button>
    </div>
</Modal>